import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Modal, ModalOptions, ModalService } from '@indice/ng-components';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  distinctUntilChanged,
  mergeMap,
  Observable,
  of,
  Subject,
  tap,
} from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  FilterDropdownComponent,
  FilterDropdownItem,
} from 'src/app/features/find-property/filter-dropdown/filter-dropdown.component';
import {
  PropertyListFilter,
  PropertySummaryResultSet,
  PropertyType,
} from 'src/app/services/portal-api.service';
import { PropertyListingService } from 'src/app/services/property.service';
import { SearchHistoryService } from 'src/app/services/search-history.service';
import { ToasterService, ToastType } from 'src/app/services/toaster.service';
import {
  SearchPropertyComponent,
  SearchView,
} from 'src/app/shared/components/autocomplete/search-property/search-property.component';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';
import { SaveSearchAnonymousModalComponent } from '../save-search-modal/save-search-anonymous-modal/save-search-anonymous-modal.component';
import { SaveSearchModalComponent } from '../save-search-modal/save-search-modal.component';
import { AdvancedFiltersDrawerComponent } from './advanced-filters-drawer/advanced-filters-drawer.component';
import { GridListCardsComponent } from './grid-list-cards/grid-list-cards.component';
import {
  RangeDropdownComponent,
  RangeDropdownItem,
} from './range-dropdown/range-dropdown.component';
import { GeocodeService } from 'src/app/core/services/geocode.service';
import { SortDropdownComponent } from '../../shared/components/sort-dropdown/sort-dropdown.component';
import { PropertyListHeader } from './property-list-header/property-list-header.component';

@Component({
  selector: 'app-find-property',
  standalone: true,
  imports: [
    CommonModule,
    GridListCardsComponent,
    SearchPropertyComponent,
    FilterDropdownComponent,
    RangeDropdownComponent,
    AdvancedFiltersDrawerComponent,
    TranslocoDirective,
    LanguageRouterLinkDirective,
    SortDropdownComponent,
    PropertyListHeader,
  ],
  templateUrl: './find-property.component.html',
  styleUrl: './find-property.component.css',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FindPropertyComponent implements OnInit, AfterViewInit {
  @ViewChild('headingElement') headingElement!: ElementRef;
  headingHeight = 48;
  toggleAdvancedFilters$: Subject<void> = new Subject();
  advancedFilterOn: boolean = false;
  searchFilter: PropertyListFilter | undefined;
  propertyTypes: FilterDropdownItem[] = [];
  showPopularAreas: boolean = true;
  selectedType: PropertyType[] | undefined;
  selectedPriceRange: RangeDropdownItem | undefined;
  selectedSizeRange: RangeDropdownItem | undefined;
  isloggedIn: boolean = false;
  public selectedItem: FilterDropdownItem | undefined = {
    key: 'mostRecent',
    label: 'Most recent',
  };
  protected sortKeyMapping: { [key: string]: string | undefined } = {
    mostRecent: 'publishedDate-',
    ascPrice: 'price+',
    descPrice: 'price-',
    ascSize: 'propertyArea+',
    descSize: 'propertyArea-',
    suggested: 'suggested',
  };
  orderByItems: FilterDropdownItem[] = [
    {
      key: 'mostRecent',
      label: 'Most recent',
    },
    {
      key: 'ascPrice',
      label: 'Ascending Price',
    },
    {
      key: 'descPrice',
      label: 'Descending Price',
    },
    {
      key: 'ascSize',
      label: 'Ascending Size',
    },
    {
      key: 'descSize',
      label: 'Descending Size',
    },
  ];
  minPriceRanges: RangeDropdownItem[] = [
    { from: 10000, label: '10.000€' },
    { from: 20000, label: '20.000€' },
    { from: 30000, label: '30.000€' },
    { from: 40000, label: '40.000€' },
    { from: 50000, label: '50.000€' },
  ];

  maxPriceRanges: RangeDropdownItem[] = [
    { to: 100000, label: '100.000€' },
    { to: 200000, label: '200.000€' },
    { to: 500000, label: '500.000€' },
    { to: 1000000, label: '1.000.000€' },
    { to: 2000000, label: '2.000.000€' },
  ];

  minSizeRanges: RangeDropdownItem[] = [
    { from: 10, label: '10 τ.μ.' },
    { from: 20, label: '20 τ.μ.' },
    { from: 30, label: '30 τ.μ.' },
    { from: 40, label: '40 τ.μ.' },
    { from: 50, label: '50 τ.μ.' },
  ];

  maxSizeRanges: RangeDropdownItem[] = [
    { to: 150, label: '150 τ.μ.' },
    { to: 200, label: '200 τ.μ.' },
    { to: 500, label: '500 τ.μ.' },
    { to: 800, label: '800 τ.μ.' },
    { to: 1000, label: '1000 τ.μ.' },
  ];

  searchFiltersCount: number = 0;
  isAnyFilterSelected: boolean = false;
  areaUnit!: string;
  private hasAreaSearch = false;

  private readonly emptySearchFilters = {
    from: undefined,
    to: undefined,
    label: '',
  };

  readonly allowedPropertyTypes: PropertyType[] = [
    PropertyType.Apartment,
    PropertyType.Villa,
    PropertyType.Maisonette,
    PropertyType.CountryHouse,
  ];

  public searchToastsTranslations: any;
  protected displayMap: boolean = true;
  protected propertyCount: number = 0;
  public queryParamSortString: string | undefined;
  isSearchOpen: boolean = false;

  public currentViewport = '';

  constructor(
    private destroyRef: DestroyRef,
    private _geocodeService: GeocodeService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private propertyListingService: PropertyListingService,
    private searchHistoryService: SearchHistoryService,
    private authService: AuthService,
    private _modalService: ModalService,
    private cdr: ChangeDetectorRef,
    private translocoService: TranslocoService,
    @Inject(ToasterService) private _toastService: ToasterService,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector
  ) {
    this.authService.isLoggedIn().subscribe((x) => (this.isloggedIn = x));

    this.translocoService
      .selectTranslateObject('searches.toasts')
      .pipe(
        tap((translations) => {
          this.searchToastsTranslations = translations;
        })
      )
      .subscribe();
  }

  ngOnInit() {
    this.translocoService.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.translateLabels();

        this.propertyTypes = this.allowedPropertyTypes.map((type) => {
          return {
            label: `find-property.propertyType.${type}`,
            key: type,
            icon: this.propertyTypeIcons(type),
            translation: `find-property.propertyType.${type}`,
          };
        });
      });

    this.activatedRoute.queryParamMap
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.updateSortingFromParams(params);
        this.hasAreaSearch = params.get('searchTerm') != null;
        this.searchFilter =
          this.propertyListingService.getSearchFiltersFromUrl(params);
        this.searchFiltersCount = this.countSelectedFilters(
          this.searchFilter,
          this.hasAreaSearch,
          true
        ).count;
        this.isAnyFilterSelected =
          this.countSelectedFilters(this.searchFilter, this.hasAreaSearch)
            .count > 0;

        this.hasUrlParams(params);

        //Type filter
        this.selectedType = this.searchFilter.type?.length
          ? this.searchFilter.type
          : [];

        //price filter
        if (
          this.searchFilter.minPrice !== undefined ||
          this.searchFilter.maxPrice !== undefined
        ) {
          let label = `${this.searchFilter.minPrice?.toString() ?? ''} - ${this.searchFilter.maxPrice?.toString() ?? ''
            }`;
          this.selectedPriceRange = {
            from: this.searchFilter.minPrice,
            to: this.searchFilter.maxPrice,
            label,
          };
        } else {
          this.selectedPriceRange = this.emptySearchFilters;
        }

        //size filter
        if (this.searchFilter.minSqMeters || this.searchFilter.maxSqMeters) {
          let label = `${this.searchFilter.minSqMeters?.toString() ?? ''} - ${this.searchFilter.maxSqMeters?.toString() ?? ''
            }`;
          this.selectedSizeRange = {
            from: this.searchFilter.minSqMeters,
            to: this.searchFilter.maxSqMeters,
            label,
          };
        } else {
          this.selectedSizeRange = this.emptySearchFilters;
        }
      });

    // Get Current Viewport: 'mobile' | 'md' | 'lg' | 'xl'
    this.currentViewport = this.getCurrentViewport();
  }

  ngAfterViewInit() {
    if (this.headingElement) {
      console.log(this.headingElement.nativeElement.offsetHeight);
      this.headingHeight = this.headingElement.nativeElement.offsetHeight;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.headingHeight = this.headingElement.nativeElement.offsetHeight;
    // Get Current Viewport: 'mobile' | 'md' | 'lg' | 'xl'
    this.currentViewport = this.getCurrentViewport();
  }

  updateSortingFromParams(params: ParamMap): void {
    const sortKey = params.get('sort');
    const keyToUse = sortKey
      ? this.findKeyByValue(this.sortKeyMapping, sortKey)
      : 'mostRecent';

    this.selectedItem = this.orderByItems.find((item) => item.key === keyToUse);

    this.queryParamSortString = this.selectedItem
      ? this.sortKeyMapping[this.selectedItem.key] || ''
      : '';
  }

  findKeyByValue = (
    keyMapping: { [key: string]: string | undefined },
    value: string | undefined
  ): string | undefined => {
    return Object.entries(keyMapping).find(([, v]) => v === value)?.[0];
  };

  objectsToTranslate() {
    this.minSizeRanges = this.objectTranslate(this.minSizeRanges);
    this.maxSizeRanges = this.objectTranslate(this.maxSizeRanges);
  }

  translateLabels() {
    this.translocoService
      .selectTranslateObject('find-property.unit.sqm')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translation) => {
        this.areaUnit = translation;
        this.objectsToTranslate();
      });
  }

  objectTranslate(object: RangeDropdownItem[]) {
    const replacementString = /τ\.μ\.|m²/g;

    const obj = object.map((range) => ({
      ...range,
      label: range.label.replace(replacementString, this.areaUnit),
    }));
    return obj;
  }

  private hasUrlParams(params: ParamMap) {
    const excludeKey = 'searchTerm';
    const keys = params.keys.filter((key) => key !== excludeKey);
    const noFilter = keys.reduce((acc, key) => {
      const value = params.get(key);
      return acc && (value === null || value === '');
    }, true);
    this.showPopularAreas = noFilter;
    if (!noFilter && this.isloggedIn) {
      this.searchHistoryService.saveHistory(params);
    }
  }

  private formatKey(key: string): string {
    const formattedKey = key
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase();

    // Capitalize the first letter of the entire phrase
    const formatted =
      formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1);
    return formatted;
  }
  public searchWithLocation(placesToSearch: any[]): PropertyListFilter {
    let filter: PropertyListFilter = new PropertyListFilter();
    let encodedMessages: string[] = [];
    let placesTerms: string[] = [];
    let encodedAreaQueryParam = '';
    let searchTerm: string = '';

    placesToSearch?.forEach((place: any) => {
      if (place?.query?.searchTerm) {
        // popular search
        encodedAreaQueryParam = place?.query?.area ?? '';
        searchTerm = place?.query?.searchTerm ?? '';
      } else {
        let encodedMessage = this._geocodeService.protoEncodeGeoSearch(place);
        encodedMessages.push(btoa(String.fromCharCode(...encodedMessage)));
        placesTerms.push(
          `${place.PlaceId}~${place.FormatedAddress}~${btoa(
            String.fromCharCode(...encodedMessage)
          )}`
        );
        searchTerm = placesTerms?.join('!');
        encodedAreaQueryParam = encodedMessages.join('!');
      }
    });

    return new PropertyListFilter({
      area: encodedAreaQueryParam,
      searchTerm: searchTerm,
    });
  }

  public selectSearchItem(placesToSelect: any[]) {
    let placesTerms: string[] = [];
    if (!placesToSelect || placesToSelect.length == 0) {
      this.router.navigate([localizeRoute('/properties', this.envInjector)], {
        queryParams: { searchTerm: undefined, area: undefined },
        queryParamsHandling: 'merge',
      });
      return;
    }
    let areaAndSearchTermFilters = this.searchWithLocation(placesToSelect);
    let filter = new PropertyListFilter({});
    if (areaAndSearchTermFilters) {
      filter.area = areaAndSearchTermFilters.area;
      filter.searchTerm = areaAndSearchTermFilters.searchTerm;
      filter.searchFromMap = undefined;
    }
    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...filter, page: 1 },
      queryParamsHandling: 'merge',
    });
  }
  searchWithTypeFilter(propertyType: any | undefined) {
    if (!propertyType || propertyType.length == 0) {
      this.router.navigate([localizeRoute('/properties', this.envInjector)], {
        queryParams: { type: undefined },
        queryParamsHandling: 'merge',
      });
      return;
    }

    let filter: PropertyListFilter = new PropertyListFilter({
      type: propertyType.join(','),
    });
    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...filter, page: 1 },
      queryParamsHandling: 'merge',
    });
  }
  searchWithPriceFilter(range: RangeDropdownItem | undefined) {
    if (!range) {
      this.router.navigate([localizeRoute('/properties', this.envInjector)], {
        queryParams: { minPrice: undefined, maxPrice: undefined },
        queryParamsHandling: 'merge',
      });
      return;
    }

    let filter: PropertyListFilter = new PropertyListFilter({
      minPrice: range.from,
      maxPrice: range.to,
    });

    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...filter, page: undefined },
      queryParamsHandling: 'merge',
    });
  }
  searchWithSizeFilter(range: RangeDropdownItem | undefined) {
    if (!range) {
      this.router.navigate([localizeRoute('/properties', this.envInjector)], {
        queryParams: { minSqMeters: undefined, maxSqMeters: undefined },
        queryParamsHandling: 'merge',
      });
      return;
    }

    let filter: PropertyListFilter = new PropertyListFilter({
      minSqMeters: range.from,
      maxSqMeters: range.to,
    });

    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...filter, page: undefined },
      queryParamsHandling: 'merge',
    });
  }
  advancedSearchHidden(val: boolean) {
    this.advancedFilterOn = false;
    if (val) {
      document.documentElement.classList.toggle('overflow-hidden');
      document.documentElement.classList.toggle('pr-2');
    }
  }
  toggleAdvancedFilers() {
    this.toggleAdvancedFilters$.next();
    this.advancedFilterOn = !this.advancedFilterOn;
    document.documentElement.classList.toggle('overflow-hidden');
    document.documentElement.classList.toggle('pr-2');
  }
  searchWithAdvancedFilters(event: {
    filter: PropertyListFilter;
    count: number;
  }) {
    this.searchFilter = event.filter;
    this.cdr.detectChanges();
    this.router.navigate([localizeRoute('/properties', this.envInjector)], {
      queryParams: { ...this.convertArrayValues(event.filter), page: 1 },
      queryParamsHandling: 'merge',
    });
  }
  removeQueryParams(): void {
    // Use navigation to clear query parameters
    this.router.navigate([this.router.url.split('?')[0]], {
      queryParams: {},
      queryParamsHandling: '',
    });
  }
  public SaveSearch() {
    let saveSearchModal: Modal<
      SaveSearchModalComponent | SaveSearchAnonymousModalComponent
    >;

    const options: ModalOptions<
      SaveSearchModalComponent | SaveSearchAnonymousModalComponent
    > = {
      keyboard: true,
      class: 'h-dvh md:h-auto',
      ...(this.isloggedIn
        ? {
          initialState: {
            SearchFilters: this.searchFilter,
          } as Partial<SaveSearchModalComponent>,
        }
        : {}),
    };

    saveSearchModal = this._modalService.show(
      this.isloggedIn
        ? SaveSearchModalComponent
        : SaveSearchAnonymousModalComponent,
      options
    );

    // Listen for the modal's onHidden event
    saveSearchModal.onHidden
      ?.pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap((result: any) => {
          if (result && result.result.success) {
            this._toastService.showBanner(
              ToastType.Success,
              this.searchToastsTranslations['searchSavedTitle'],
              this.searchToastsTranslations['whereToFind'],
              3500
            );
          } else {
            Error('No success in modal result, skipping toast');
          }
          return of(null); // Return observable
        })
      )
      .subscribe();
  }

  private propertyTypeIcons(
    propertyType: PropertyType | undefined
  ): string | undefined {
    const defaultIcon = 'icon-Apartment';

    switch (propertyType) {
      case PropertyType.Apartment:
        return 'icon-Apartment';
      case PropertyType.Maisonette:
        return 'icon-maisonette';
      case PropertyType.Business:
        return 'icon-briefcase';
      case PropertyType.Villa:
        return 'icon-villas';
      default:
        return defaultIcon;
    }
  }

  private convertArrayValues(obj: { [key: string]: any }): {
    [key: string]: any;
  } {
    const result: { [key: string]: any } = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (Array.isArray(value)) {
          result[key] = (value as any[]).join(',');
        } else if (typeof result[key] === 'boolean' && result[key] === true) {
        } else {
          result[key] = value;
        }
      }
    }

    return result;
  }
  private getAllowedKeysForCurrentViewport(): string[] {
    // # Dynamically exclude certain keys from the count badge depending on the current viewport size
    switch (this.currentViewport) {
      case 'mobile':
        return ['searchTerm', 'page'];
      case 'md':
        return ['type', 'searchTerm', 'page'];
      case 'lg':
        return ['type', 'minPrice', 'maxPrice', 'searchTerm', 'page'];
      case 'xl':
        return ['type', 'minPrice', 'maxPrice', 'minSqMeters', 'maxSqMeters', 'searchTerm', 'page'];
      default:
        return ['type', 'minPrice', 'maxPrice', 'minSqMeters', 'maxSqMeters', 'searchTerm', 'page'];
    }
  }
  private isAdvancedFilter(key: string): boolean {
    const keys = this.getAllowedKeysForCurrentViewport();
    return !keys.includes(key);
  }

  private countSelectedFilters(
    filterObj: { [key: string]: any },
    hasAreaSearch: boolean,
    onlyAdvanced: boolean = false
  ): { count: number; filter: { [key: string]: any } } {
    let count = 0;

    for (const key in filterObj) {
      if (onlyAdvanced && !this.isAdvancedFilter(key)) {
        continue;
      }
      const value = filterObj[key];

      // Count each element in arrays
      if (Array.isArray(value)) {
        count += value.length; // Add the length of the array to the count
      }
      // Count boolean values if they are true
      else if (typeof value === 'boolean' && value === true) {
        count++;
      }
      // Count numbers (assuming any specified number is considered a selected filter)
      else if (typeof value === 'number') {
        count++;
      } else if (typeof value === 'string') {
        if (key !== 'area' || (key === 'area' && this.hasAreaSearch)) {
          count++;
        }
      }
    }

    return {
      count: count,
      filter: filterObj,
    };
  }

  private getCurrentViewport(): 'mobile' | 'md' | 'lg' | 'xl' {
    const width = window.innerWidth;

    if (width >= 1280) {
      return 'xl';
    } else if (width >= 1024) {
      return 'lg';
    } else if (width >= 768) {
      return 'md';
    } else {
      return 'mobile';
    }
  }
}
